import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useGameStatusStore } from 'modules/Room/game-status/store/game-status-store';

import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import { InfoBannerType } from 'shared/components/ui/InfoBanner/InfoBanner';
import { MoveStatusRenderer } from 'shared/components/ui/MoveStatusRenderer/MoveStatusRenderer';
import { MoveStatusType } from 'shared/components/ui/MoveStatusRenderer/MoveStatusRenderer.types';

export const useRevealStatusText = (
  showTimer: boolean,
  showCloseButton: boolean,
  technicalDefeatError: boolean,
) => {
  const { revealGameStatus } = useGameStatusStore();
  const { t } = useTranslation();

  const Reveal = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_REVEAL}
        title={t('modals.waitYourReveal.confirm')}
      />
    ),
    [t],
  );

  const RevealWithTimer = useCallback(
    () => (
      <MoveStatusRenderer
        type={InfoBannerType.WARNING}
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_REVEAL}
        title={t('modals.waitYourReveal.confirm')}
        // hideIconOnMobile
      />
    ),
    [t],
  );

  const ErrorText = useCallback(
    () => (
      <MoveStatusRenderer title={t('modals.waitYourReveal.again')} type={InfoBannerType.WARNING} />
    ),
    [t],
  );

  const ErrorTextWithTimer = useCallback(
    () => (
      <MoveStatusRenderer
        title={t('modals.waitYourReveal.revealAgain')}
        type={InfoBannerType.WARNING}
      />
    ),
    [t],
  );

  const RejectText = useCallback(
    () => (
      <MoveStatusRenderer
        title={t('modals.waitYourReveal.decline')}
        type={InfoBannerType.WARNING}
      />
    ),
    [t],
  );

  const RejectTextWithTimer = useCallback(
    () => (
      <MoveStatusRenderer
        title={t('modals.waitYourReveal.timerTip')}
        type={InfoBannerType.WARNING}
      />
    ),
    [t],
  );

  const TechnicalDefeatAllStates = useCallback(
    () => (
      <MoveStatusRenderer
        title={t('modals.waitYourReveal.technicalDefeat')}
        type={InfoBannerType.WARNING}
      />
    ),
    [t],
  );

  const TechnicalDefeatAllStatesError = useCallback(
    () => (
      <MoveStatusRenderer
        title={t('modals.waitYourReveal.technicalDefeatErrorExplanation')}
        type={InfoBannerType.WARNING}
      />
    ),
    [t],
  );

  const getTextComponent = useCallback(
    () => (
      <>
        <AnimatedTransition
          show={
            !revealGameStatus.error && !revealGameStatus.reject && !showCloseButton && showTimer
          }
        >
          <RevealWithTimer />
        </AnimatedTransition>
        <AnimatedTransition
          show={
            !revealGameStatus.error && !revealGameStatus.reject && !showCloseButton && !showTimer
          }
        >
          <Reveal />
        </AnimatedTransition>
        <AnimatedTransition show={revealGameStatus.error && !showCloseButton && showTimer}>
          <ErrorTextWithTimer />
        </AnimatedTransition>
        <AnimatedTransition show={revealGameStatus.error && !showCloseButton && !showTimer}>
          <ErrorText />
        </AnimatedTransition>
        <AnimatedTransition show={revealGameStatus.reject && !showCloseButton && showTimer}>
          <RejectTextWithTimer />
        </AnimatedTransition>
        <AnimatedTransition show={revealGameStatus.reject && !showCloseButton && !showTimer}>
          <RejectText />
        </AnimatedTransition>
        <AnimatedTransition show={showCloseButton && technicalDefeatError}>
          <TechnicalDefeatAllStatesError />
        </AnimatedTransition>
        <AnimatedTransition show={showCloseButton && !technicalDefeatError}>
          <TechnicalDefeatAllStates />
        </AnimatedTransition>
      </>
    ),
    [revealGameStatus, showCloseButton, showTimer, technicalDefeatError],
  );

  return { getTextComponent };
};
